import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Bookmark, ExternalLink, MapPin, Share2, Copy, Store, Heart, Globe } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import Countdown from "../components/Countdown";
import DealCard from "../components/DealCard";
import WebDealCard from "../components/WebDealCard";
import ReviewsSection from "../components/ReviewsSection";

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, toggleBookmark } = useAuth();
  const [deal, setDeal] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followBusy, setFollowBusy] = useState(false);
  const [following, setFollowing] = useState(false);
  const [similarWeb, setSimilarWeb] = useState([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [similarExcluded, setSimilarExcluded] = useState([]);

  const bookmarked = user?.bookmarks?.includes(deal?.id);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/deals/${id}`);
        setDeal(data.deal);
        setRelated(data.related || []);
        if (data.deal?.merchant) {
          try {
            const res = await api.get(`/merchants/${encodeURIComponent(data.deal.merchant)}/following`);
            setFollowing(!!res.data?.following);
          } catch (e) { /* not fatal */ }
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          toast.error("Deal not found or no longer active.");
          navigate("/");
        } else {
          toast.error(formatApiError(err));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  // Quietly fetch cheaper alternatives from around the web
  useEffect(() => {
    if (!deal?.id) return;
    setSimilarLoading(true);
    setSimilarWeb([]);
    (async () => {
      try {
        const { data } = await api.get(`/deals/${deal.id}/similar-web`, { params: { limit: 3 } });
        setSimilarWeb(data.deals || []);
        setSimilarExcluded(data.excluded_merchants || []);
      } catch (similarErr) {
        // Silent — this is a bonus feature, don't disrupt the page. Log for debugging.
        console.warn("similar-web fetch failed", similarErr);
      } finally {
        setSimilarLoading(false);
      }
    })();
  }, [deal?.id]);

  const copyLink = async () => {
    const base = `${window.location.origin}/deals/${id}`;
    const url = user ? `${base}?ref=${encodeURIComponent(user.id)}` : base;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(user ? "Link copied — friends who sign up earn you 30 days of Pro." : "Link copied to clipboard.");
    } catch (e) {
      toast.error("Couldn't copy — long-press the URL to share.");
    }
  };

  const pingClickThrough = () => {
    if (!user || !deal) return;
    api.post(`/deals/${deal.id}/click`).catch(() => {});
  };

  const handleBookmark = async () => {
    if (!user) { toast.error("Log in to save deals."); navigate("/login"); return; }
    try { await toggleBookmark(deal.id); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const handleFollowToggle = async () => {
    if (!user) { navigate("/login"); return; }
    if (!deal?.merchant) return;
    setFollowBusy(true);
    try {
      const { data } = await api.post(`/merchants/${encodeURIComponent(deal.merchant)}/follow`);
      const isFollowing = data.action === "followed";
      setFollowing(isFollowing);
      toast.success(isFollowing ? `Following ${deal.merchant}.` : `Unfollowed ${deal.merchant}.`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setFollowBusy(false); }
  };

  if (loading) {
    return <div className="max-w-6xl mx-auto px-6 py-16 text-xs font-mono uppercase tracking-widest text-black/40">Loading deal…</div>;
  }
  if (!deal) return null;

  const expired = deal.expires_at && new Date(deal.expires_at).getTime() < Date.now();

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid={`deal-detail-${deal.id}`}>
      <Link
        to={`/category/${deal.category}`}
        className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 hover:text-[#E63946]"
        data-testid="detail-back-link"
      >
        <ArrowLeft className="w-3 h-3" strokeWidth={2.5} />
        Back to {deal.category}
      </Link>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-8">
        {/* Image column */}
        <div className="md:col-span-3">
          <div className={`relative border border-black/10 bg-black/5 aspect-[16/10] overflow-hidden ${expired ? "opacity-60" : ""}`}>
            {deal.image_url ? (
              <img src={deal.image_url} alt={deal.title} className={`w-full h-full object-cover ${expired ? "grayscale" : ""}`} />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-black/20 font-serif text-8xl">◇</div>
            )}
            {deal.discount && (
              <div className="absolute top-4 left-4 bg-[#E63946] text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5" data-testid="detail-discount">
                {deal.discount}
              </div>
            )}
            {deal.expires_at && (
              <div className="absolute bottom-4 left-4"><Countdown expiresAt={deal.expires_at} /></div>
            )}
          </div>
        </div>

        {/* Info column */}
        <div className="md:col-span-2 flex flex-col">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-black/60 mb-3 flex-wrap">
            <Link to={`/category/${deal.category}`} className="hover:text-[#E63946]">{deal.category}</Link>
            <span className="w-1 h-1 bg-black/30 rounded-full" />
            <Link
              to={`/merchant/${encodeURIComponent(deal.merchant)}`}
              className="inline-flex items-center gap-1 hover:text-[#E63946]"
              data-testid="detail-merchant-link"
            >
              <Store className="w-3 h-3" strokeWidth={2} />
              {deal.merchant}
            </Link>
            {deal.location_city && (
              <>
                <span className="w-1 h-1 bg-black/30 rounded-full" />
                <span className="inline-flex items-center gap-1 text-[#E63946]">
                  <MapPin className="w-3 h-3" strokeWidth={2} />
                  {deal.location_city}
                </span>
              </>
            )}
          </div>
          <h1 className="font-serif font-black text-3xl md:text-4xl leading-tight tracking-tighter mb-4" data-testid="detail-title">
            {deal.title}
          </h1>
          <p className="text-base font-serif text-black/70 leading-relaxed mb-6">{deal.description}</p>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <a
              href={deal.url}
              target="_blank"
              rel="noopener noreferrer"
              onMouseDown={pingClickThrough}
              onAuxClick={pingClickThrough}
              className="inline-flex items-center gap-2 bg-[#111111] text-white text-xs font-bold uppercase tracking-[0.25em] px-5 py-3 hover:bg-[#E63946]"
              data-testid="detail-shop-btn"
            >
              Shop the deal
              <ExternalLink className="w-4 h-4" strokeWidth={2} />
            </a>
            <button
              onClick={handleBookmark}
              className={`inline-flex items-center gap-2 border-2 border-black text-xs font-bold uppercase tracking-[0.25em] px-4 py-3 ${bookmarked ? "bg-black text-white" : "bg-white text-black hover:bg-black hover:text-white"}`}
              data-testid="detail-bookmark-btn"
            >
              <Bookmark className="w-4 h-4" strokeWidth={2} fill={bookmarked ? "currentColor" : "none"} />
              {bookmarked ? "Saved" : "Save"}
            </button>
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-2 border-2 border-black bg-white text-black text-xs font-bold uppercase tracking-[0.25em] px-4 py-3 hover:bg-black hover:text-white"
              data-testid="detail-copy-link-btn"
              title="Copy shareable link"
            >
              <Copy className="w-4 h-4" strokeWidth={2} />
              Copy link
            </button>
          </div>

          {deal.merchant && (
            <div className="border-t border-black/10 pt-5 flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/40 mb-1">Merchant</div>
                <Link to={`/merchant/${encodeURIComponent(deal.merchant)}`} className="font-serif text-lg font-bold hover:text-[#E63946]">
                  {deal.merchant}
                </Link>
              </div>
              <button
                onClick={handleFollowToggle}
                disabled={followBusy}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.2em] border-2 disabled:opacity-50 ${
                  following
                    ? "bg-[#111111] text-white border-[#111111] hover:bg-[#E63946] hover:border-[#E63946]"
                    : "bg-white text-black border-black hover:bg-black hover:text-white"
                }`}
                data-testid="detail-follow-btn"
              >
                <Heart className="w-3.5 h-3.5" strokeWidth={2} fill={following ? "currentColor" : "none"} />
                {following ? "Following" : "Follow"}
              </button>
            </div>
          )}

          {deal.submitted_by_name && (
            <div className="mt-5 text-[10px] font-mono uppercase tracking-widest text-black/50">
              Submitted by <span className="text-[#E63946] font-bold">{deal.submitted_by_name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-16 border-t border-black/10 pt-10" data-testid="detail-reviews">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/40 mb-1"><Share2 className="w-3 h-3 inline mr-1" strokeWidth={2} />Community reviews</div>
        <h2 className="font-serif text-2xl md:text-3xl font-black tracking-tight mb-6">What shoppers said</h2>
        <ReviewsSection deal={deal} active={true} variant="inline" />
      </section>

      {/* Related from same merchant */}
      {related.length > 0 && (
        <section className="mt-16 border-t border-black/10 pt-10" data-testid="detail-related">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/40 mb-1">More from {deal.merchant}</div>
          <h2 className="font-serif text-2xl md:text-3xl font-black tracking-tight mb-6">You may also like</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((r) => (<DealCard key={r.id} deal={r} />))}
          </div>
        </section>
      )}

      {/* Cheaper alternatives from around the web */}
      {(similarLoading || similarWeb.length > 0) && (
        <section className="mt-16 border-t border-black/10 pt-10" data-testid="detail-similar-web">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700 mb-1 flex items-center gap-1.5">
            <Globe className="w-3 h-3" strokeWidth={2.5} /> Live · Google Search
          </div>
          <h2 className="font-serif text-2xl md:text-3xl font-black tracking-tight mb-2">
            Cheaper elsewhere?
          </h2>
          <p className="text-sm text-black/50 mb-6">
            We just crawled the web for comparable deals
            {similarExcluded.length > 0 && (
              <> at other merchants (excluding <span className="font-mono text-black/70">{similarExcluded.join(", ")}</span>)</>
            )}
            .
          </p>
          {similarLoading ? (
            <div className="border border-dashed border-black/15 p-8 text-center" data-testid="similar-web-loading">
              <div className="text-xs font-mono uppercase tracking-widest text-black/40">
                Comparing prices across the web…
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="similar-web-grid">
              {similarWeb.map((d) => (<WebDealCard key={d.id} deal={d} />))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
